import React from 'react';
import { Frame, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_FRAME,
  FRAME_COLORS,
  FRAME_GRADIENTS,
  FRAME_LIMITS,
  isFrameActive,
  type FrameBackground,
  type VideoFrameStyle,
} from '../lib/videoFrame';
import { PanelHeader, PanelSectionTitle } from './EditorPanelParts';

interface VideoFramePanelProps {
  frame: VideoFrameStyle;
  onChange: (frame: VideoFrameStyle) => void;
}

const BACKGROUNDS: FrameBackground[] = ['none', 'color', 'gradient', 'blur'];

const swatchStyle = (selected: boolean, background: string): React.CSSProperties => ({
  width: 26, height: 26, borderRadius: 6, cursor: 'pointer', padding: 0, background,
  border: selected ? '2px solid var(--primary, #4f46e5)' : '1px solid var(--controlsBorder, #d0d0d0)',
  boxShadow: selected ? '0 0 0 2px var(--background, #fff) inset' : 'none',
});

/** Background, padding, rounded corners and shadow around the video (preview and export). */
export function VideoFramePanel({ frame, onChange }: VideoFramePanelProps) {
  const { t } = useTranslation();
  const set = (patch: Partial<VideoFrameStyle>) => onChange({ ...frame, ...patch });
  const pct = (value: number) => Math.round(value * 100);

  return (
    <section className="ve-frame-panel" aria-label={t('video_editor_frame.title', 'Frame')}>
      <PanelHeader
        icon={<Frame size={16} />}
        title={t('video_editor_frame.title', 'Frame')}
        subtitle={t('video_editor_panel.whole_video', 'Applies to the whole video')}
        action={isFrameActive(frame) && (
          <button
            type="button"
            className="ve-frame-reset"
            onClick={() => onChange(DEFAULT_FRAME)}
            title={t('video_editor_frame.reset', 'Reset')}
            aria-label={t('video_editor_frame.reset', 'Reset')}
          >
            <RotateCcw size={13} aria-hidden />
          </button>
        )}
      />

      <PanelSectionTitle>{t('video_editor_frame.background', 'Background')}</PanelSectionTitle>
      <div>
        <div className="ve-frame-segments" role="group" aria-label={t('video_editor_frame.background', 'Background')}>
          {BACKGROUNDS.map((bg) => (
            <button
              key={bg}
              type="button"
              aria-pressed={frame.background === bg}
              className={`ve-frame-segment${frame.background === bg ? ' active' : ''}`}
              onClick={() => set({ background: bg })}
            >
              {t(`video_editor_frame.bg_${bg}`, { none: 'None', color: 'Color', gradient: 'Gradient', blur: 'Blur' }[bg])}
            </button>
          ))}
        </div>
      </div>

      {frame.background === 'color' && (
        <div className="ve-frame-swatches" role="group" aria-label={t('video_editor_frame.color', 'Color')}>
          {FRAME_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={c}
              aria-pressed={frame.color.toLowerCase() === c}
              style={swatchStyle(frame.color.toLowerCase() === c, c)}
              onClick={() => set({ color: c })}
            />
          ))}
          <input
            type="color"
            value={frame.color}
            onChange={(e) => set({ color: e.target.value })}
            aria-label={t('video_editor_frame.custom_color', 'Custom color')}
            title={t('video_editor_frame.custom_color', 'Custom color')}
            className="ve-frame-color-input"
          />
        </div>
      )}

      {frame.background === 'gradient' && (
        <div className="ve-frame-swatches" role="group" aria-label={t('video_editor_frame.gradient', 'Gradient')}>
          {FRAME_GRADIENTS.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-label={g.id}
              aria-pressed={frame.gradient === g.id}
              style={swatchStyle(frame.gradient === g.id, `linear-gradient(135deg, ${g.from}, ${g.to})`)}
              onClick={() => set({ gradient: g.id })}
            />
          ))}
        </div>
      )}

      <PanelSectionTitle>{t('video_editor_panel.shape', 'Shape')}</PanelSectionTitle>
      <label className="ve-props-label">
        <span className="ve-frame-slider-head">
          {t('video_editor_frame.padding', 'Padding')}
          <span className="ve-props-size-value">{pct(frame.padding)}%</span>
        </span>
        <input
          type="range"
          min={0}
          max={pct(FRAME_LIMITS.padding)}
          step={1}
          value={pct(frame.padding)}
          onChange={(e) => set({ padding: Number(e.target.value) / 100 })}
        />
      </label>

      <label className="ve-props-label">
        <span className="ve-frame-slider-head">
          {t('video_editor_frame.corners', 'Rounded corners')}
          <span className="ve-props-size-value">{pct(frame.radius)}%</span>
        </span>
        <input
          type="range"
          min={0}
          max={pct(FRAME_LIMITS.radius)}
          step={1}
          value={pct(frame.radius)}
          onChange={(e) => set({ radius: Number(e.target.value) / 100 })}
        />
      </label>

      <label className="ve-frame-check">
        <input type="checkbox" checked={frame.shadow} onChange={(e) => set({ shadow: e.target.checked })} />
        {t('video_editor_frame.shadow', 'Shadow')}
      </label>

      {frame.background === 'none' && frame.padding > 0 && (
        <p className="ve-props-empty">{t('video_editor_frame.hint_background', 'Choose a background to fill the space around the video.')}</p>
      )}
    </section>
  );
}
